import React, { useState } from 'react';
import { API_BASE as API } from '../../lib/apiBase';
import {
  Crown, Calendar, Clock, DollarSign, Image, Sparkles,
  Loader2, AlertCircle, Check, Wand2, Upload, X
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════════════
   CREATE TICKETED SHOW FORM — Artist Backstage Panel
   
   Features:
   - Create a new ticketed show (min +12h, min 3€)
   - Generate banner with DALL-E
   - Upload custom banner
   - Premium golden UI
═══════════════════════════════════════════════════════════════════════════════ */


const MIN_PRICE = 3;
const MIN_HOURS = 12;

export const CreateTicketedShowForm = ({ 
  token, 
  artistId,
  onSuccess, 
  onCancel,
  lang = 'es' 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduled_at: '',
    price: MIN_PRICE,
    banner_url: null,
    category: 'music'
  });
  const [bannerPrompt, setBannerPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatingBanner, setGeneratingBanner] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Calculate minimum datetime (12h from now)
  const minDateTime = new Date(Date.now() + MIN_HOURS * 60 * 60 * 1000);
  const minDateTimeStr = minDateTime.toISOString().slice(0, 16);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    }));
  };

  const handleGenerateBanner = async () => {
    if (!bannerPrompt.trim()) {
      setError(lang === 'es' ? 'Describe el banner que quieres generar' : 'Describe the banner you want to generate');
      return;
    }

    setGeneratingBanner(true);
    setError(null);

    try {
      // First create the show to get an ID
      const createRes = await fetch(`${API}/api/live/ticketed-show`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          scheduled_at: new Date(formData.scheduled_at).toISOString()
        })
      });

      if (!createRes.ok) {
        const data = await createRes.json();
        throw new Error(data.detail || 'Error al crear el show');
      }

      const show = await createRes.json();

      // Now generate banner
      const bannerRes = await fetch(`${API}/api/live/ticketed-show/${show.id}/generate-banner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ prompt: bannerPrompt })
      });

      if (!bannerRes.ok) {
        const data = await bannerRes.json();
        throw new Error(data.detail || 'Error al generar banner');
      }

      const bannerData = await bannerRes.json();
      
      setFormData(prev => ({ ...prev, banner_url: bannerData.banner_url }));
      setSuccess(true);
      
      setTimeout(() => {
        onSuccess?.({ ...show, banner_url: bannerData.banner_url });
      }, 1500);

    } catch (e) {
      setError(e.message);
    }

    setGeneratingBanner(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError(lang === 'es' ? 'El título es obligatorio' : 'Title is required');
      return;
    }
    if (!formData.description.trim()) {
      setError(lang === 'es' ? 'La descripción es obligatoria' : 'Description is required');
      return;
    }
    if (!formData.scheduled_at) {
      setError(lang === 'es' ? 'Selecciona fecha y hora' : 'Select date and time');
      return;
    }
    if (formData.price < MIN_PRICE) {
      setError(lang === 'es' ? `Precio mínimo: ${MIN_PRICE}€` : `Minimum price: ${MIN_PRICE}€`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/api/live/ticketed-show`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          scheduled_at: new Date(formData.scheduled_at).toISOString()
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Error al crear el show');
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(data);
      }, 1500);

    } catch (e) {
      setError(e.message);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-[#d4a853]/20 flex items-center justify-center mx-auto mb-6">
          <Check size={40} className="text-[#d4a853]" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">
          {lang === 'es' ? '¡Show Creado!' : 'Show Created!'}
        </h3>
        <p className="text-white/60">
          {lang === 'es' 
            ? 'Tu show con ticket está programado y visible en el lobby.'
            : 'Your ticketed show is scheduled and visible in the lobby.'}
        </p>
      </div>
    );
  }

  return (
    <div 
      data-testid="create-ticketed-show-form"
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(145deg, #1a1505 0%, #0d0a02 100%)',
        border: '1px solid rgba(212,168,83,0.3)'
      }}
    >
      {/* Header */}
      <div className="p-6 border-b border-[#d4a853]/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#d4a853]/20 flex items-center justify-center">
            <Crown size={24} className="text-[#d4a853]" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              {lang === 'es' ? 'Crear Show con Ticket' : 'Create Ticketed Show'}
            </h3>
            <p className="text-sm text-[#d4a853]/80">
              {lang === 'es' ? 'Evento exclusivo de pago' : 'Exclusive paid event'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {lang === 'es' ? 'Título del Show *' : 'Show Title *'}
          </label>
          <input
            data-testid="show-title-input"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder={lang === 'es' ? 'Ej: Noche Acústica Exclusiva' : 'Ex: Exclusive Acoustic Night'}
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-[#d4a853]/30 text-white placeholder-white/30 focus:border-[#d4a853] outline-none transition"
            maxLength={100}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            {lang === 'es' ? 'Descripción *' : 'Description *'}
          </label>
          <textarea
            data-testid="show-description-input"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder={lang === 'es' ? 'Describe qué hace especial este show...' : 'Describe what makes this show special...'}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-[#d4a853]/30 text-white placeholder-white/30 focus:border-[#d4a853] outline-none transition resize-none"
            maxLength={1000}
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2 flex items-center gap-2">
              <Calendar size={14} className="text-[#d4a853]" />
              {lang === 'es' ? 'Fecha y Hora *' : 'Date & Time *'}
            </label>
            <input
              data-testid="show-datetime-input"
              type="datetime-local"
              name="scheduled_at"
              value={formData.scheduled_at}
              onChange={handleChange}
              min={minDateTimeStr}
              className="w-full px-4 py-3 rounded-xl bg-black/40 border border-[#d4a853]/30 text-white focus:border-[#d4a853] outline-none transition"
            />
            <p className="text-xs text-white/40 mt-1">
              {lang === 'es' ? `Mínimo ${MIN_HOURS}h de antelación` : `Minimum ${MIN_HOURS}h in advance`}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2 flex items-center gap-2">
              <DollarSign size={14} className="text-[#d4a853]" />
              {lang === 'es' ? 'Precio *' : 'Price *'}
            </label>
            <div className="relative">
              <input
                data-testid="show-price-input"
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min={MIN_PRICE}
                step="0.50"
                className="w-full px-4 py-3 pr-12 rounded-xl bg-black/40 border border-[#d4a853]/30 text-white focus:border-[#d4a853] outline-none transition"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#d4a853] font-medium">€</span>
            </div>
            <p className="text-xs text-white/40 mt-1">
              {lang === 'es' ? `Mínimo ${MIN_PRICE}€` : `Minimum ${MIN_PRICE}€`}
            </p>
          </div>
        </div>

        {/* Banner Section */}
        <div className="p-4 rounded-xl bg-black/30 border border-[#d4a853]/20">
          <label className="block text-sm font-medium text-white/70 mb-3 flex items-center gap-2">
            <Image size={14} className="text-[#d4a853]" />
            {lang === 'es' ? 'Banner Promocional' : 'Promotional Banner'}
          </label>

          {/* Current Banner Preview */}
          {formData.banner_url && (
            <div className="relative mb-4 rounded-xl overflow-hidden aspect-video">
              <img 
                src={formData.banner_url} 
                alt="Banner preview" 
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, banner_url: null }))}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* Generate with AI */}
          <div className="mb-3">
            <p className="text-xs text-white/50 mb-2 flex items-center gap-1">
              <Wand2 size={12} className="text-[#d4a853]" />
              {lang === 'es' ? 'Generar con IA (DALL-E)' : 'Generate with AI (DALL-E)'}
            </p>
            <div className="flex gap-2">
              <input
                data-testid="banner-prompt-input"
                type="text"
                value={bannerPrompt}
                onChange={(e) => setBannerPrompt(e.target.value)}
                placeholder={lang === 'es' ? 'Describe el banner: ej. concierto íntimo con velas...' : 'Describe the banner: ex. intimate concert with candles...'}
                className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-[#d4a853]/20 text-sm text-white placeholder-white/30 focus:border-[#d4a853] outline-none transition"
              />
              <button
                type="button"
                data-testid="generate-banner-btn"
                onClick={handleGenerateBanner}
                disabled={generatingBanner || !bannerPrompt.trim()}
                className="px-4 py-2 rounded-lg bg-[#d4a853]/20 border border-[#d4a853]/40 text-[#d4a853] text-sm font-medium hover:bg-[#d4a853]/30 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {generatingBanner ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                {lang === 'es' ? 'Generar' : 'Generate'}
              </button>
            </div>
          </div>

          {/* OR Upload */}
          <div className="flex items-center gap-3 text-xs text-white/30">
            <div className="flex-1 h-px bg-white/10" />
            <span>{lang === 'es' ? 'o sube tu imagen' : 'or upload your image'}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          
          <label className="mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-[#d4a853]/30 text-[#d4a853]/60 text-sm cursor-pointer hover:border-[#d4a853]/50 hover:text-[#d4a853] transition">
            <Upload size={16} />
            {lang === 'es' ? 'Subir imagen (16:9)' : 'Upload image (16:9)'}
            <input type="file" accept="image/*" className="hidden" />
          </label>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center gap-2">
            <AlertCircle size={16} className="text-red-400" />
            <span className="text-sm text-red-400">{error}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-white/60 font-medium hover:text-white hover:border-white/20 transition"
          >
            {lang === 'es' ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            type="submit"
            data-testid="create-show-submit-btn"
            disabled={loading}
            className="flex-1 py-3 rounded-xl font-bold text-black transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{
              background: 'linear-gradient(135deg, #d4a853 0%, #b8942a 100%)',
              boxShadow: '0 4px 15px rgba(212,168,83,0.4)'
            }}
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin text-black" />
            ) : (
              <>
                <Crown size={18} />
                {lang === 'es' ? 'Crear Show' : 'Create Show'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
